import { HttpClient } from '@angular/common/http';
import { EventEmitter, inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  AccountMessageTopic,
  MessageCenterCategory,
  SendAccountMessagePayload
} from '@k12/models';
import type { Message } from '@k12/app-components';
import { SignalRService } from '@k12/app-services';
import { environment } from '../../../environments/environment';

interface AccountMessageTopicReadPayload {
  accountMessageTopicId: string;
  isRead: boolean;
}

interface ArchiveAccountMessageTopicsPayload {
  accountMessageTopicId: string;
}

interface UpdateAccountMessageTopicPayload {
  accountMessageTopicId: string;
  assignedToId?: string | null;
  childCategoryId?: string | number | null;
  parentCategoryId?: string | number;
}

interface MessageAutoAssignmentConfigurationRequestPayload {
  assignedToUserId: string | null;
  messageChildCategoryId?: string | null;
  messageAutoAssignmentConfigurationId?: string;
  messageParentCategoryId: string;
  previousAssignedToUserId?: string | null;
  programPartnerTypeId: number;
}

interface MessageAutoAssignmentConfigurationResponse {
  id?: string;
  messageAutoAssignmentConfigurationId?: string;
}

interface MessageAutoAssignmentConfiguration {
  assignedAdminAccountId?: string | null;
  assignedAdminName?: string;
  assignedToUserId?: string | null;
  childCategoryId?: string;
  messageAutoAssignmentConfigurationId?: string;
  messageChildCategoryId?: string;
  messageParentCategoryId?: string;
  parentCategoryId?: string;
  programPartnerTypeId?: number;
}

interface NewMessageSignalREvent {
  msg: unknown;
  user: unknown;
}

interface MessageCenterPartnerRecord {
  householdId?: string;
  id?: string;
  programPartnerId?: string;
  providerId?: string;
  [key: string]: unknown;
}

interface MessageCenterTopicPayload extends MessageCenterPartnerRecord {
  assignedToId?: string;
  assignedToName?: string;
  businessName?: string;
  childCategoryId?: string;
  childSubject?: string;
  dateArchived?: Date | string | null;
  dateCreated?: string | Date;
  firstName?: string;
  householdName?: string;
  lastMessageDate?: string | Date;
  lastName?: string;
  messageDescription?: string;
  messageSubject?: string;
  messages?: Message[];
  middleName?: string;
  ownerName?: string;
  parentCategoryId?: string;
  participantId?: string;
  programPartnerName?: string;
  programPartnerTypeId?: number;
  provider?: string;
  providerName?: string;
  schoolName?: string;
  topicId?: string;
  unreadCount?: number;
}

interface MessageCenterTopicsResponse {
  households?: MessageCenterPartnerRecord[];
  providers?: MessageCenterPartnerRecord[];
  schools?: MessageCenterPartnerRecord[];
  topics: MessageCenterTopicPayload[];
}

@Injectable({
  providedIn: 'root'
})
export class MessageCenterService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly signalRService = inject(SignalRService);
  private readonly ngDestroy = new Subject<void>();

  // signalR Events
  public unReadUpdate = new EventEmitter<string>();
  public unclassified = new EventEmitter<string>();
  public newUnassignedTopic = new EventEmitter<string>();
  public topicAssigned = new EventEmitter<string>();
  public messageReply = new EventEmitter<string>();
  public topicArchived = new EventEmitter<string>();
  public newMessage = new EventEmitter<NewMessageSignalREvent>();
  public messageRead = new EventEmitter<unknown>();

  constructor() {
    setTimeout(() => {
      this.registerSignalREvents();
    }, 100);
  }

  public ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  public getProvidersAndTopicsList(): Observable<MessageCenterTopicsResponse> {
    return this.http.get<MessageCenterTopicsResponse>(`${environment.adminApiUrl}/admcomms/providersAndTopicsList`);
  }

  public sendAccountMessage(payload: SendAccountMessagePayload): Observable<Message> {
    return this.http.post<Message>(`${environment.adminApiUrl}/admcomms/accountMessage`, payload);
  }

  public createAccountMessageTopic(payload: Record<string, unknown>): Observable<unknown> {
    return this.http.post<unknown>(`${environment.adminApiUrl}/admcomms/accountMessageTopic`, payload);
  }

  public admToggleAccountMessageTopicAsRead(payload: AccountMessageTopicReadPayload): Observable<unknown> {
    return this.http.post<unknown>(`${environment.adminApiUrl}/admcomms/accountMessageTopicRead`, payload);
  }

  public archiveAccountMessageTopics(payload: ArchiveAccountMessageTopicsPayload): Observable<unknown> {
    return this.http.post<unknown>(`${environment.adminApiUrl}/admcomms/accountMessageTopicArchive`, payload);
  }

  public getAllTopics(programPartnerTypeId: number | null): Observable<MessageCenterCategory[]> {
    let url = `${environment.adminApiUrl}/comms/allMessagingTopics`;
    if (programPartnerTypeId !== null) {
      url = `${environment.adminApiUrl}/comms/messagingTopics/${programPartnerTypeId}`;
    }
    return this.http.get<MessageCenterCategory[]>(url);
  }

  public searchMessages(searchTerm: string): Observable<MessageCenterTopicsResponse> {
    const encodedSearchTerm = encodeURIComponent(searchTerm);
    return this.http.get<MessageCenterTopicsResponse>(
      `${environment.adminApiUrl}/comms/allAccountMessagesAndTopics?searchTerm=${encodedSearchTerm}`
    );
  }

  public updateAccountMessageTopic(payload: UpdateAccountMessageTopicPayload): Observable<string> {
    return this.http.put(`${environment.adminApiUrl}/admcomms/accountMessageTopicUpdate`, payload, {
      responseType: 'text'
    });
  }

  public getAccountMessageTopics(programPartnerId: string): Observable<AccountMessageTopic[]> {
    return this.http.get<AccountMessageTopic[]>(
      `${environment.adminApiUrl}/admcomms/accountMessageTopics?programPartnerId=${programPartnerId}`
    );
  }

  public GetMessageAutoAssignmentConfigurations(): Observable<MessageAutoAssignmentConfiguration[]> {
    return this.http.get<MessageAutoAssignmentConfiguration[]>(
      `${environment.adminApiUrl}/comms/messageAutoassignmentConfigurations`
    );
  }

  public UpsertMessageAutoAssignmentConfiguration(
    payload: MessageAutoAssignmentConfigurationRequestPayload,
    reassignUnresolvedMessages: boolean
  ): Observable<MessageAutoAssignmentConfigurationResponse> {
    return this.http.post<MessageAutoAssignmentConfigurationResponse>(
      `${environment.adminApiUrl}/comms/messageAutoassignmentConfiguration?reassignUnresolvedMessages=${reassignUnresolvedMessages}`,
      payload
    );
  }

  public DeleteMessageAutoAssignmentConfiguration(id: string): Observable<unknown> {
    return this.http.delete<unknown>(`${environment.adminApiUrl}/comms/messageAutoassignmentConfiguration/${id}`);
  }

  public GetTopicsForProgramPartnerType(
    programPartnerTypeId: number | null,
    apiUrl?: string
  ): Observable<MessageCenterCategory[]> {
    const baseApiUrl = apiUrl ?? environment.adminApiUrl;
    return this.http.get<MessageCenterCategory[]>(`${baseApiUrl}/comms/messagingTopics/${programPartnerTypeId}`);
  }

  private registerSignalREvents(): void {
    this.signalRService.signalRConnectionStatus.pipe(takeUntil(this.ngDestroy)).subscribe((res) => {
      if (res === 'open') {
        this.attachSignalREvents();
      } else {
        this.detachSignalREvents();
      }
    });
  }

  private attachSignalREvents(): void {
    this.signalRService.hubConnection?.on('UnReadUpdate', (msg: string) => {
      this.unReadUpdate.emit(msg);
    });

    this.signalRService.hubConnection?.on('Unclassified', (msg: string) => {
      this.unclassified.emit(msg);
    });

    this.signalRService.hubConnection?.on('NewUnassignedTopic', (msg: string) => {
      this.newUnassignedTopic.emit(msg);
    });

    this.signalRService.hubConnection?.on('TopicAssigned', (msg: string) => {
      this.topicAssigned.emit(msg);
    });

    this.signalRService.hubConnection?.on('MessageReply', (msg: string) => {
      this.messageReply.emit(msg);
    });

    this.signalRService.hubConnection?.on('TopicArchived', (msg: string) => {
      this.topicArchived.emit(msg);
    });

    this.signalRService.hubConnection?.on('MessageRead', (msg: string) => {
      const parsedMessage: unknown = JSON.parse(msg);
      this.messageRead.emit(parsedMessage);
    });

    this.signalRService.hubConnection?.on('NewMessage', (user: unknown, msg: unknown) => {
      this.newMessage.emit({ user, msg });
    });
  }

  private detachSignalREvents(): void {
    this.signalRService.hubConnection?.off('UnReadUpdate');
    this.signalRService.hubConnection?.off('Unclassified');
    this.signalRService.hubConnection?.off('NewUnassignedTopic');
    this.signalRService.hubConnection?.off('TopicAssigned');
    this.signalRService.hubConnection?.off('MessageReply');
    this.signalRService.hubConnection?.off('TopicArchived');
    this.signalRService.hubConnection?.off('MessageRead');
    this.signalRService.hubConnection?.off('NewMessage');
  }
}
